import { useState, useEffect } from "react";
import { Menu, Phone } from "lucide-react";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { siteData } from "../data/mock";

const messageTemplate = `Hi Dr. Kang, I'd like to book an appointment for [Name] at your [Location] office. My preferred time is [Date/Time].`;

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleBookAppointment = () => {
    const encodedMessage = encodeURIComponent(messageTemplate);
    window.location.href = `sms:${siteData.clinic.smsNumber}?body=${encodedMessage}`;
  };

  return (
    <header
      role="banner"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors transition-shadow duration-300 ${isScrolled
          ? "bg-[#FFFAF5]/95 backdrop-blur-xl shadow-sm border-b border-stone-200/60"
          : "bg-[#FFFAF5]/70 backdrop-blur-md"
        }`}
    >
      <nav
        role="navigation"
        aria-label="Main navigation"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        <div className="flex items-center justify-between h-16 lg:h-20">
          <a
            href="#"
            className="flex items-center gap-2.5 group"
            aria-label="Pure Wave Acupuncture — Home"
          >
            <div className="w-9 h-9 rounded-lg bg-[#232323] flex items-center justify-center group-hover:bg-[#353535] transition-colors duration-200">
              <span className="text-white font-bold text-base tracking-tight">PW</span>
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-[#232323] text-sm leading-tight tracking-tight">
                Pure Wave
              </span>
              <span className="text-[11px] text-[#5E8C61] leading-tight font-medium">
                Acupuncture
              </span>
            </div>
          </a>

          <div className="hidden lg:flex items-center gap-8">
            {siteData.navigation.map((item) => (
              <a
                key={item.label}
                href={item.href}
                target={item.external ? "_blank" : undefined}
                rel={item.external ? "noopener noreferrer" : undefined}
                className="text-[13px] font-medium text-stone-600 hover:text-[#5E8C61] transition-colors duration-200 relative after:absolute after:bottom-[-2px] after:left-0 after:w-0 after:h-[2px] after:bg-[#5E8C61] after:transition-all after:duration-200 hover:after:w-full"
              >
                {item.label}
              </a>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <a
              href={siteData.hero.secondaryCta.url}
              className="flex items-center gap-1.5 text-sm text-stone-600 hover:text-[#5E8C61] transition-colors duration-200"
              aria-label="Call our office at (714) 681-2785"
            >
              <Phone className="w-4 h-4" />
              <span className="font-medium">{siteData.clinic.phone}</span>
            </a>
            <Button
              onClick={handleBookAppointment}
              className="bg-[#232323] hover:bg-[#353535] text-white rounded-full px-6 h-10 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
              data-testid="header-book-appointment-btn"
            >
              Book Appointment
            </Button>
          </div>

          <div className="lg:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Open navigation menu"
                  className="hover:bg-stone-100"
                >
                  <Menu className="w-5 h-5 text-stone-700" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-[#FFFAF5] p-6">
                <div className="flex flex-col gap-1 mt-8">
                  {siteData.navigation.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      target={item.external ? "_blank" : undefined}
                      rel={item.external ? "noopener noreferrer" : undefined}
                      onClick={() => setMobileOpen(false)}
                      className="text-base font-medium text-stone-700 hover:text-[#5E8C61] hover:bg-[#F0F4ED] px-3 py-3 rounded-lg transition-colors duration-200"
                    >
                      {item.label}
                    </a>
                  ))}
                  <div className="border-t border-stone-200 pt-6 mt-4">
                    <a
                      href={siteData.hero.secondaryCta.url}
                      className="flex items-center gap-2 text-stone-600 mb-5 px-3"
                      aria-label="Call our office"
                    >
                      <Phone className="w-4 h-4 text-[#5E8C61]" />
                      <span className="text-sm font-medium">{siteData.clinic.phone}</span>
                    </a>
                    <Button
                      onClick={() => {
                        setMobileOpen(false);
                        handleBookAppointment();
                      }}
                      className="w-full bg-[#232323] hover:bg-[#353535] text-white rounded-full h-11"
                    >
                      Book Appointment
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </header>
  );
};
